<script>
import lazyLoadComponent from '@router/lazyload-component'
import BaseButtonDialog from '@components/BaseButtonDialog'

import { Portal } from 'portal-vue'
import { isTempleModule, isLightModule } from '@utils'

import {
  genderFilter,
  orderStatusFilter,
  orderSourceFilter,
  orderPaymentMethodMap
} from '@utils/filter-value-to-text'
import formatDate, { formatLunarDate } from '@utils/format-date'

export default {
  name: 'OrderDetail',
  components: {
    BaseButton: () => lazyLoadComponent(import('@components/BaseButton')),
    BaseStateLabel: () =>
      lazyLoadComponent(import('@components/BaseStateLabel')),
    BaseCollapse: () => lazyLoadComponent(import('@components/BaseCollapse')),
    OrderForm: () => lazyLoadComponent(import('@components/OrderForm')),
    OrderChangeStatusDialog: () =>
      lazyLoadComponent(import('@components/OrderChangeStatusDialog')),
    OrderMemberDialog: () =>
      lazyLoadComponent(import('@components/OrderMemberDialog')),
    BaseButtonDialog,
    NotificationWrite: () =>
      lazyLoadComponent(import('@components/NotificationWrite')),
      Portal
  },
  filters: {
    orderStatusFilter,
    genderFilter,
    orderSourceFilter,
    formatDate,
    formatLunarDate
  },
  props: {
    defaultValue: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      mode: 'view', // view 檢視、edit 編輯
      shareDialogVisible: false,
      cancelDialogVisible: false,
      memberDialogVisible: false,
      tryingSendEmail: false,
      tryingSendSMS: false,
      shareLink: '',
      writeDialogVisible: false,
      orderPaymentMethodMap,
      mapStatusName: {
        '1': 'QR Code',
        '2': '付款',
        '3': '訂單受理中',
        '4': '點燈序號',
      }
    }
  },
  computed: {
    isPaid() {
      return this.defaultValue.status === 1
    },
    isPaidByPrepaid() {
      return this.defaultValue.paymentMethod === 'prepaid'
    },
    isEmptyByOrderExtrafields() {
      return this.defaultValue.byOrderExtrafields instanceof Array
    },
    isEmptyBySizeExtrafields() {
      if (this.defaultValue.customerInfos.length === 0) return true
      return (
        this.defaultValue.customerInfos[0].bySizeExtrafields instanceof Array
      )
    },
    productType() {
      return this.defaultValue.productType
    },
    priceTitle() {
      const list = [] 
      this.defaultValue.params.forEach((item)=>{
        list.push(...Array(item.sizeTotal).fill(item.title))
      })
      return list
    },
    isUnpaid() {
      return this.defaultValue.status === 2
    },
    paidAt() {
      if(this.defaultValue.paidAt === null) return '無'
      return formatDate(this.defaultValue.paidAt, 'dateTime')
    },
    paymentExpiredDate() {
      if(this.defaultValue.allowExpired === 1) return '無（可逾期付款）'
      return formatDate(this.defaultValue.paymentExpiredDate, 'dateTime')
    }
  },
  methods: {
    isTempleModule,
    isLightModule,
    updateMode(value) {
      this.mode = value
    },
    async tryToSendEmail() {
      this.tryingSendEmail = true
      try {
        await this.$store.dispatch('order/sendLink', {
          orderId: this.defaultValue.id,
          router: 'email',
        })
        this.$alert(
          `已發送${this.isPaid ? ' QR Code ' : '付款'}連結至使用人 Email`,
          'Email 發送成功！',
          {
            confirmButtonText: '確認',
            center: true,
            callback: () => {
              this.tryingSendEmail = false
            },
          }
        )
      } catch (error) {
        this.tryingSendEmail = false
      }
    },
    async tryToSendSMS() {
      this.tryingSendSMS = true
      try {
        await this.$store.dispatch('order/sendLink', {
          orderId: this.defaultValue.id,
          router: 'sms',
        })
        this.$alert(
          `已發送${this.isPaid ? ' QR Code ' : '付款'}連結簡訊至使用人電話`,
          '簡訊發送成功！',
          {
            confirmButtonText: '確認',
            center: true,
            callback: () => {
              this.tryingSendSMS = false
            },
          }
        )
      } catch (error) {
        this.tryingSendSMS = false
      }
    },

    async handleStore() {
      const updatedValue = await this.$store.dispatch(
        'order/fetchSingle',
        this.defaultValue.id
      )
      this.$emit('update:defaultValue', updatedValue)
      this.$emit('update')
      this.updateMode('view')
    },
    copyShareLink() {
      const ghostInput = document.createElement('INPUT')
      document.body.appendChild(ghostInput)
      ghostInput.setAttribute('value', this.shareLink)
      ghostInput.select()
      document.execCommand('copy')
      document.body.removeChild(ghostInput)
      this.$message('連結複製成功')
    },
    handleShareLink(platform) {
      // <!-- <a href=”fb-messenger://share/?link= https%3A%2F%2Fdevelopers.facebook.com%2Fdocs%2Fsharing%2Freference%2Fsend-dialog&app_id=123456789”>Send In Messenger</a> -->
      const platformLinkMap = {
        facebook: `https://www.facebook.com/sharer/sharer.php?u=${this.shareLink}`,
        line: `https://social-plugins.line.me/lineit/share?url=${this.shareLink}`,
      }
      window.open(platformLinkMap[platform], '_blank')
    },
    showShareDialog() {
      this.shareDialogVisible = true
    },
    showCancelDialog() {
      this.cancelDialogVisible = true
    },
    showMemberDialog() {
      this.memberDialogVisible = true
    },
    async tryToGetShareLink() {
      this.shareLink = await this.$store.dispatch(
        'order/getLink',
        this.defaultValue.id
      )
    },
    closeDialog() {
      this.writeDialogVisible = false
    },
    handleCommand(command) {
      this[command]()
    }
  },
}
</script>

<template>
  <div>
    <h3>{{ `#${defaultValue.id}` }}</h3>
    <p :class="$style.meta">
      <span>{{ defaultValue.created | formatDate('dateTime') }} 成立訂單</span>
      <BaseStateLabel state="info" text
        >訂單來源：{{ defaultValue.source | orderSourceFilter }}</BaseStateLabel
      >
    </p>
    <BaseStateLabel :state="defaultValue.status | orderStatusFilter('color')">{{
      defaultValue.status | orderStatusFilter('text')
    }}</BaseStateLabel>

    <div :class="$style.header">
      <h5 :class="$style.title">{{ defaultValue.productName }}</h5>
      <div v-if="isLightModule(productType)">{{ defaultValue.date | formatLunarDate }}</div>
      <div v-else-if="productType === 'treasury'">{{ defaultValue.date | formatDate }}</div>
      <div v-else>{{ defaultValue.date | formatDate }} {{ defaultValue.time }}</div>
    </div>

    <!-- 編輯訂單 -->
    <OrderForm
      v-if="mode === 'edit'"
      :default-value="defaultValue"
      @store="handleStore"
      @cancel="updateMode('view')"
    />
    <div v-else :class="$style.params">
      <div :class="$style['params__item']">
        <div>訂購項目</div>
        <div>售價</div>
        <div>數量</div>
        <div v-if="!isPaidByPrepaid">小計</div>
      </div>
      <div
        v-for="param in defaultValue.params"
        :key="param.id"
        :class="$style['params__item']"
      >
        <div>{{ param.title }}</div>
        <div>{{ param.price }}</div>
        <div>{{ `x${param.qty}` }}</div>
        <div v-if="!isPaidByPrepaid">{{ param.price * param.qty }}</div>
      </div>

      <template v-if="isPaidByPrepaid">
        <div :class="$style.total">
          <div :class="$style['total__item']">
            <div>使用方案</div>
            <div>{{ defaultValue.prepaid.title }}</div>
          </div>
          <div :class="$style['total__item']">
            <div>使用次數</div>
            <div>{{ defaultValue.groupSize }}</div>
          </div>
        </div>
      </template>
      <template v-else>
        <div :class="$style.total">
          <div :class="$style['total__item']">
            <div>小計</div>
            <div>{{ defaultValue.priceBase }}</div>
          </div>
          <div
            v-if="defaultValue.hasOwnProperty('coupon')"
            :class="[$style['total__item'], $style['total__item--discount']]"
          >
            <div>{{
              `${defaultValue.coupon.title}（${defaultValue.coupon.code}）`
            }}</div>
            <div>{{ `-${defaultValue.coupon.discountValue}` }}</div>
          </div>
        </div>
        <div :class="$style.total">
          <div :class="$style['total__item']">
            <div>付款金額</div>
            <div>{{ defaultValue.priceTotal }}</div>
          </div>
        </div>
      </template>
    </div>

    <template v-if="mode === 'view'">
      <BaseCollapse title="訂單變更紀錄" style="margin-bottom: 10px;">
        <el-timeline>
          <el-timeline-item
            v-for="log in defaultValue.history"
            :key="log.id"
            :timestamp="log.created | formatDate('dateTime')"
            type="primary"
            placement="top"
          >
            {{ log.description }}
            <div v-if="log.note">{{ log.note }}</div>
          </el-timeline-item>
        </el-timeline>
      </BaseCollapse>
      
      <BaseCollapse title="付款資訊" :class="$style.payment">
        <ul style="padding: 0 25px;padding-bottom: 10px;">
          <li><span class="title">付款方式</span><span class="content">{{ orderPaymentMethodMap[defaultValue.paymentMethod] || '無'}}</span></li>
          <li><span class="title">付款時間</span><span class="content">{{ paidAt }}</span></li>
          <li v-if="defaultValue.paymentMethod !== 'Vacc' && !isLightModule(productType)"><span class="title">付款期限</span><span class="content">{{ paymentExpiredDate }}</span></li>
          <template v-if="defaultValue.Vacc">
            <li style="border-top: 1px solid #dcdfe6;"><span class="title">轉帳銀行</span><span class="content">{{`${defaultValue.Vacc.Bank} ${defaultValue.Vacc.BankCode}`}}</span></li>
            <li><span class="title">轉帳帳號</span><span class="content">{{ defaultValue.Vacc.VACCNo }}</span></li>
            <li><span class="title">轉帳期限</span><span class="content">{{ defaultValue.paymentExpiredDate | formatDate('dateTime') }}</span></li>
          </template>
        </ul>
      </BaseCollapse>

      <h5 :class="$style.title">登記人資訊</h5>
      <ul :class="$style.info">
        <li
          >{{ defaultValue.fullName }}
          {{ defaultValue.gender | genderFilter }}</li
        >
        <li>{{ `${defaultValue.phoneCode} ${defaultValue.phoneNumber}` }}</li>
        <li>{{ defaultValue.email }}</li>
      </ul>
      <h5 :class="$style.title">登記人備註</h5>
      <p>{{ defaultValue.note || '目前尚無備註' }}</p>

      <BaseCollapse
        v-if="!isEmptyByOrderExtrafields"
        title="參與資訊"
        :class="$style['info-byOrder']"
      >
        <ul
          v-for="(byOrderExtrafield,
          byOrderExtrafieldIndex) in defaultValue.byOrderExtrafields"
          :key="`byOrderExtrafieldIndex-${byOrderExtrafieldIndex}`"
          :class="$style.question"
        >
          <li class="title">{{ byOrderExtrafield.title }}</li>
          <li>{{ byOrderExtrafield.value }}</li>
        </ul>
      </BaseCollapse>

      <BaseCollapse v-if="!isEmptyBySizeExtrafields" :title="`${isTempleModule(productType) ? '登記' : '參與人'}資訊`">
        <div
          v-for="(customerInfo, index) in defaultValue.customerInfos"
          :key="`customerInfo-${index}`"
          :class="$style['question-list']"
        >
          <div class="customer">
            <BaseIcon name="user" icon-style="fas" />
            {{ isTempleModule(productType) ? priceTitle[index] : `參與人${index + 1}`}}
          </div>
          <template v-for="(field, bySizeIndex) in customerInfo.bySizeExtrafields">
            <ul
              v-if="field.adminEdit === 0"
              :key="`customerInfo-${index}-${bySizeIndex}`"
              :class="$style.question"
            >
              <li class="title">{{ field.title }}</li>
              <li>{{ field.value }}</li>
            </ul>
            <ul
              v-else-if="field.adminEdit === 1 && !isUnpaid"
              :key="`customerInfo-${index}-${bySizeIndex}`"
              :class="$style.question"
            >
              <li class="title">{{ field.title }}</li>
              <li v-if="field.type === 'light_image'">
                <img :src="field.value"/>
              </li>
              <li v-else>{{ field.value }}</li>
            </ul>
          </template>
          
        </div>
      </BaseCollapse>

      <Portal to="drawer-header">
        <template v-if="defaultValue.status !== 0">
          <el-button type="danger" @click="showCancelDialog">取消訂單</el-button>
          <el-button @click="updateMode('edit')">編輯訂單</el-button>
          <el-button @click="writeDialogVisible = true">撰寫通知</el-button>
          <el-dropdown trigger="click" :hide-on-click="false" @command="handleCommand" >
            <el-button style="margin-left: 10px;">更多 <BaseIcon name="caret-down"/></el-button>
            <el-dropdown-menu slot="dropdown">
              <el-dropdown-item command="showMemberDialog">查看客戶資訊</el-dropdown-item>
              <template v-if="defaultValue.status !== 1">
                <el-dropdown-item command="showShareDialog" >分享{{ mapStatusName[defaultValue.status] }}頁面連結</el-dropdown-item>
                <el-dropdown-item v-loading="tryingSendEmail" command="tryToSendEmail">發送{{ mapStatusName[defaultValue.status] }}連結至使用人Email</el-dropdown-item>
              </template>
              <template v-else-if="defaultValue.status === 1 && productType === 'puja'">
                <el-dropdown-item command="showShareDialog" >分享{{ mapStatusName[defaultValue.status] }}頁面連結</el-dropdown-item>
                <el-dropdown-item v-loading="tryingSendEmail" command="tryToSendEmail">發送{{ mapStatusName[defaultValue.status] }}連結至使用人Email</el-dropdown-item>
              </template>
              
            </el-dropdown-menu>
          </el-dropdown>
        </template>
        <template v-else>
          <el-button @click="showMemberDialog">查看客戶資訊</el-button>
        </template>
      </Portal>

      <!-- <div :class="$style.buttons" >
        <BaseButton icon="clipboard-list" @click="showMemberDialog"
          >查看客戶資訊</BaseButton
        >
        <template v-if="defaultValue.status !== 0">
          <BaseButton icon="mail-bulk" @click="writeDialogVisible = true"
            >撰寫通知</BaseButton
          >
          <BaseButton
            icon="paper-plane"
            :loading="tryingSendEmail"
            @click="tryToSendEmail"
            >發送{{ isPaid ? ' QR Code ' : '付款' }}連結至使用人
            Email</BaseButton
          >
          <BaseButton icon="share-alt" @click="showShareDialog"
            >分享{{ isPaid ? 'QR Code' : '付款' }}頁面連結</BaseButton
          >
          <BaseButton icon="edit" @click="updateMode('edit')"
            >編輯訂單</BaseButton
          >
          <BaseButton icon="ban" @click="showCancelDialog">取消訂單</BaseButton>
        </template>
      </div> -->
      
      <OrderChangeStatusDialog
        :visible.sync="cancelDialogVisible"
        :order="{ ...defaultValue, status: 0 }"
        @cancelled="handleStore"
      />
      <el-dialog
        title="分享連結"
        :visible.sync="shareDialogVisible"
        top="0"
        width="40%"
        append-to-body
        lock-scroll
        @open="tryToGetShareLink"
      >
        <div slot="title">
          <h5>{{ `#${defaultValue.id}` }}</h5>
          <ul>
            <li
              >使用人：{{ defaultValue.fullName }}
              {{ defaultValue.gender | genderFilter }}</li
            >
            <li
              >訂購內容：{{ defaultValue.productName }}
              {{ defaultValue.date | formatDate }} {{ defaultValue.time }}</li
            >
          </ul>
        </div>

        <div :class="$style['share__buttons']">
          <el-button
            type="text"
            :class="$style['share__button']"
            @click="handleShareLink('facebook')"
          >
            <BaseIcon
              :class="$style['share__icon']"
              name="facebook-f"
              icon-style="fab"
            />
          </el-button>

          <el-button
            type="text"
            :class="$style['share__button']"
            @click="handleShareLink('line')"
          >
            <BaseIcon
              :class="$style['share__icon']"
              name="line"
              icon-style="fab"
            />
          </el-button>
          <el-button
            type="text"
            :loading="tryingSendSMS"
            :class="$style['share__button']"
            @click="tryToSendSMS"
          >
            <BaseIcon :class="$style['share__icon']" name="sms" />
          </el-button>
          <div :class="$style['share__button']">
            <el-button circle @click="copyShareLink">
              <BaseIcon name="copy" />
            </el-button>
            <div :class="$style['share__label']">複製連結</div>
          </div>
        </div>
      </el-dialog>
      <OrderMemberDialog
        :visible.sync="memberDialogVisible"
        :member-id="defaultValue.memberId"
      />
      <BaseButtonDialog
        :only-dialog="true"
        :title="'撰寫通知'"
        :dialog-visible.sync="writeDialogVisible"
        :destroy="true"
        floating
      >
        <NotificationWrite
          :selected-order-ids="[defaultValue.id]"
          :source-name="'order'"
          :source-order="defaultValue"
          @close="closeDialog"
        />
      </BaseButtonDialog>
    </template>
  </div>
</template>

<style lang="scss" module>
@import '@design';
.meta {
  display: flex;
  justify-content: space-between;
}
.header {
  margin-bottom: 15px;
}
.title:not(:first-child) {
  margin-top: 30px;
}
.info {
  padding-inline-start: 0;
  > li {
    padding: 5px 0;
  }
}
.params {
  padding: 5px 15px;
  font-size: 14px;
  line-height: 40px;
  background: white;

  &:not(:last-child) {
    margin-bottom: 10px;
  }
  &__item {
    display: flex;
    justify-content: space-between;

    &:first-child {
      color: $color-text-placeholder;
    }
    > * {
      flex: 1;
      &:first-child {
        flex: 2;
      }
      &:not(:first-child) {
        text-align: right;
      }
    }
  }
}
.total {
  border-top: 1px solid $color-border-base;
  &__item {
    display: flex;
    justify-content: space-between;

    &--discount {
      color: $color-state-danger;
    }
    &:not(.total__item--discount) > *:first-child {
      color: $color-text-placeholder;
    }
  }
}

.buttons {
  margin-top: 30px;
  border-top: 1px solid $color-border-base;
  border-bottom: 1px solid $color-border-base;
}
.share {
  &__buttons {
    display: flex;
    align-items: center;
    text-align: center;
  }
  &__button {
    flex: 1;
  }
  &__icon {
    font-size: 3rem;
  }
  &__label {
    margin-top: 10px;
  }
}
.info-byOrder {
  margin-bottom: 15px;
  margin-top: 35px;
}
.question-list {
  border-bottom: 1px solid lightgrey;
  margin-bottom: 20px;
  padding-bottom: 20px;
  :global {
    .customer {
      margin: 0 0 16px 0;
      color: #53c2cc;
      vertical-align: middle;
      svg {
        margin-right: 8px;
        margin-left: 2px;
        font-size: 11px;
        vertical-align: baseline;
      }
    }
    ul:last-child {
      padding-bottom: 0;
      margin-bottom: 0;
    }
  }
  &:last-child {
    border-bottom: none;
    margin-bottom: 0px;
  }
}
.question {
  padding-left: 24.2px;
  padding-bottom: 10px;
  margin-bottom: 15px;
  :global {
    .title {
      margin-bottom: 10px;
      color: darkgray;
    }
  }
}
.payment {
  :global {
    li {
      line-height: 40px;
    }
    .title {
      margin-right: 20px;
      color: darkgray;
    }
  }
}
</style>
